import { createBackoffEventSource } from "@redotech/redo-merchant-app-common/stream/backoff-event-source";
import { Schema } from "effect";
import { createEventSource } from "eventsource-client";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from "react";

/**
 * Factory for creating a typed notification stream provider and hook.
 *
 * This creates a fully typed provider/hook pair for a specific notification schema.
 * No type casting required - everything is properly typed from the schema.
 *
 * @example
 * ```typescript
 * const { Provider, useNotificationStream } = createNotificationStreamProvider<TeamNotification>();
 * ```
 */
export function createNotificationStreamProvider<
  TNotification extends { _tag: string },
>() {
  type EventType = TNotification["_tag"];
  type EventOfType<K extends EventType> = Extract<TNotification, { _tag: K }>;
  type Handler<K extends EventType> = (event: EventOfType<K>) => void;
  type Subscribe = <K extends EventType>(
    event: K,
    handler: Handler<K>,
  ) => () => void;

  // Type predicate to narrow notification type based on event type
  function isEventOfType<K extends EventType>(
    notification: TNotification,
    eventType: K,
  ): notification is EventOfType<K> {
    return notification._tag === eventType;
  }

  function dispatchToHandlers<K extends EventType>(
    eventType: K,
    notification: TNotification,
    handlers: Set<Handler<K>>,
  ): void {
    if (isEventOfType(notification, eventType)) {
      for (const handler of handlers) {
        try {
          handler(notification);
        } catch (error) {
          console.error(`Error dispatching notification to handler: ${error}`);
        }
      }
    }
  }

  type HandlersMap = Partial<{ [K in EventType]: Set<Handler<K>> }>;

  const NotificationStreamContext = createContext<Subscribe | null>(null);

  function NotificationStreamProvider({
    url,
    token,
    schema,
    children,
  }: {
    url: string;
    token?: string;
    schema: Schema.Schema<TNotification, string>;
    children: ReactNode;
  }) {
    const listenersRef = useRef<HandlersMap>({});

    const subscribe: Subscribe = useCallback((event, handler) => {
      const currentHandlers = listenersRef.current[event];
      if (!currentHandlers) {
        listenersRef.current[event] = new Set([handler]);
      } else {
        currentHandlers.add(handler);
      }

      return () => {
        listenersRef.current[event]?.delete(handler);
      };
    }, []);

    useEffect(() => {
      if (!token) {
        return;
      }

      const handle = createBackoffEventSource({
        logLabel: "notification-stream",
        connect: async ({ onConnected, onDisconnected }) => {
          const es = createEventSource({
            url,
            headers: { Authorization: `Bearer ${token}` },
            onConnect: onConnected,
            onScheduleReconnect: () => {
              es.close();
              onDisconnected();
            },
            onMessage: ({ data }) => {
              const result = Schema.decodeUnknownEither(schema)(data, {
                onExcessProperty: "ignore",
              });
              if (result._tag === "Left") {
                return;
              }
              const notification = result.right;
              const eventType: EventType = notification._tag;
              const handlers = listenersRef.current[eventType];
              if (handlers) {
                dispatchToHandlers(eventType, notification, handlers);
              }
            },
          });
          return { close: () => es.close() };
        },
      });

      return () => handle.close();
    }, [url, token, schema]);

    return (
      <NotificationStreamContext.Provider value={subscribe}>
        {children}
      </NotificationStreamContext.Provider>
    );
  }

  function useNotificationStream<K extends EventType>(
    event: K,
    handler: Handler<K>,
  ): void {
    const subscribe = useContext(NotificationStreamContext);
    if (!subscribe) {
      throw new Error(
        "useNotificationStream must be used within NotificationStreamProvider",
      );
    }

    const handlerRef = useRef(handler);
    handlerRef.current = handler;

    useEffect(() => {
      const stableHandler: Handler<K> = (data) => handlerRef.current(data);
      return subscribe(event, stableHandler);
    }, [subscribe, event]);
  }

  return { Provider: NotificationStreamProvider, useNotificationStream };
}
